import { Vehicle } from 'core/models/vehicle';
import { VehicleConfiguration } from 'core/models/vehicleConfiguration';
import { VehicleConfigurationRepository } from 'core/repositories/VehicleConfigurationRepository';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';

const VEHICLE_CONFIGURATIONS_KEY = 'jp.tier4.drive.autoware.vehicleId.configurations';

class VehicleConfigurationRepositoryImpl implements VehicleConfigurationRepository {
    private readonly vehicleConfigurations = new Map<Vehicle.Id, VehicleConfiguration>();
    private readonly vehicleConfigurationsListener = createKeyedListenerManager<Vehicle.Id, (config: VehicleConfiguration | undefined) => void>();

    constructor(private storage: Storage = localStorage) {
        this.loadConfigurations(this.vehicleConfigurations);
    }

    private loadConfigurations<Id>(configs: Map<Id, VehicleConfiguration>) {
        const key = VEHICLE_CONFIGURATIONS_KEY;

        const raw = this.storage.getItem(key);
        if (!raw) return;

        try {
            const json = JSON.parse(raw) as [Id, VehicleConfiguration][];
            // TODO: validation on json
            json.forEach(([id, config]) => {
                configs.set(id, config);
            });
        } catch (reason) {
            console.error(reason, raw);
            throw new Error('broken vehicle configurations');
        }
    }

    private saveConfigurations<Id>(configs: Map<Id, VehicleConfiguration>) {
        const key = VEHICLE_CONFIGURATIONS_KEY;

        this.storage.setItem(key, JSON.stringify([...configs.entries()]));
    }

    async getVehicleConfiguration(vehicleId: Vehicle.Id): Promise<VehicleConfiguration | undefined> {
        return this.vehicleConfigurations.get(vehicleId);
    }

    async setVehicleConfiguration(vehicleId: Vehicle.Id, config: Partial<VehicleConfiguration>): Promise<void> {
        const prev = this.vehicleConfigurations.get(vehicleId) ?? {};
        const updated = { ...prev, ...config };
        this.vehicleConfigurations.set(vehicleId, updated);

        this.saveConfigurations(this.vehicleConfigurations);

        this.vehicleConfigurationsListener.forEach(vehicleId, listener => listener(updated));
    }

    async clearVehicleConfiguration(vehicleId: Vehicle.Id): Promise<void> {
        this.vehicleConfigurations.delete(vehicleId);

        this.saveConfigurations(this.vehicleConfigurations);

        this.vehicleConfigurationsListener.forEach(vehicleId, listener => listener(undefined));
    }

    addVehicleConfigurationListener(vehicleId: Vehicle.Id, listener: (config: VehicleConfiguration | undefined) => void) {
        this.vehicleConfigurationsListener.addListener(vehicleId, listener);
    }

    removeVehicleConfigurationListener(vehicleId: Vehicle.Id, listener: (config: VehicleConfiguration | undefined) => void) {
        this.vehicleConfigurationsListener.removeListener(vehicleId, listener);
    }
}

export { VehicleConfigurationRepositoryImpl as VehicleConfigurationRepository };
